import { useMemo } from "react";
import { useArenaAccessForArenaQuery } from "~/graphql/supergraphHooks";
import { type ArenaAccessForArenaInput } from "~/graphql/supergraphTypes";
import {
  useDateFormatterWithTimezone,
  useShortDateFormatter,
} from "~/pages/UserAndRoles/utils/dateFormatter";
import { toGlobalId } from "~/utils/graphql-helpers";
import { WorkspaceUserData } from "../types";

export const useFetchArenaAccesses = (filters: ArenaAccessForArenaInput) => {
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();
  const { shortDateFormatter } = useShortDateFormatter();
  const useGetWorkspaceUsersQueryResult = useArenaAccessForArenaQuery({
    variables: {
      filters: { ...filters, active: true },
    },
    // skips if invalid is passed
    skip: toGlobalId("ArenaObjectType", -1) === filters.arenaId,
  });

  const rowData = useMemo(() => {
    const users: WorkspaceUserData[] =
      useGetWorkspaceUsersQueryResult.data?.arenaAccessForArena?.output.map((user) => ({
        id: user.id,
        arenaName: user.arenaName,
        arenaId: user.arenaId,
        arenaDomain: user.arenaDomain,
        globalUserId: user.globalUserId,
        adminArenaUserId: user.adminArenaUserId,
        userName: user.userName,
        userEmail: user.userEmail,
        roleGroupName: user.roleGroupName,
        accessType: user.accessType,
        startDate: shortDateFormatter(user.startDate),
        endDate: shortDateFormatter(user.endDate ?? undefined),
        lastAccessTimestamp: formatDateWithTimezone(user.lastAccessTimestamp ?? undefined),
        active: user.active,
      })) ?? [];

    return users;
  }, [useGetWorkspaceUsersQueryResult.data, formatDateWithTimezone, shortDateFormatter]);

  return {
    rowData: rowData,
    useGetWorkspaceUsersQueryResult,
  };
};
